import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { FiEdit2, FiEye, FiEyeOff, FiLock, FiUser } from 'react-icons/fi';
import { authAPI, departmentAPI, userAPI } from '../utils/api';
import { getUser, saveUser } from '../utils/auth';
import './ProfilePage.css';

const ProfilePage = () => {
  const [user, setUser] = useState(() => getUser());
  const roleType = user?.role?.roleType || user?.roleType || 'N/A';
  const [departments, setDepartments] = useState([]);

  const [isProfileEditMode, setIsProfileEditMode] = useState(false);
  const [isPasswordEditMode, setIsPasswordEditMode] = useState(false);

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || '');

  const [profileSubmitting, setProfileSubmitting] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await departmentAPI.getAllDepartments();
        const rows = Array.isArray(response?.data) ? response.data : [];
        setDepartments(rows);
      } catch {
        setDepartments([]);
      }
    };

    fetchDepartments();
  }, []);

  const permissionsText = useMemo(() => {
    if (roleType === 'ADMIN') return 'Full access';
    if (roleType === 'EXCO') return 'Read-only access';
    if (roleType === 'EDITOR') return 'Department editor access';
    return 'N/A';
  }, [roleType]);

  const departmentLabel = departments.length
    ? departments.map((department) => department.name).join(', ')
    : 'No department assigned';

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');

    if (!firstName.trim() || !lastName.trim()) {
      setProfileError('First name and last name are required.');
      return;
    }

    try {
      setProfileSubmitting(true);
      const response = await userAPI.updateUser(user?.id, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phoneNumber.trim()
      });

      const updatedUser = {
        ...user,
        firstName: response?.data?.firstName || firstName.trim(),
        lastName: response?.data?.lastName || lastName.trim(),
        phoneNumber: response?.data?.phoneNumber ?? phoneNumber.trim()
      };

      setUser(updatedUser);
      saveUser(updatedUser);
      setProfileSuccess('Profile updated successfully.');
      setIsProfileEditMode(false);
    } catch (err) {
      setProfileError(err?.message || 'Failed to update profile.');
    } finally {
      setProfileSubmitting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please fill all password fields.');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }

    if (newPassword.length > 128) {
      setPasswordError('New password cannot exceed 128 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirm password do not match.');
      return;
    }

    try {
      setPasswordSubmitting(true);
      await authAPI.changePassword(oldPassword, newPassword);
      setPasswordSuccess('Password changed successfully.');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setIsPasswordEditMode(false);
    } catch (err) {
      setPasswordError(err?.message || 'Failed to change password.');
    } finally {
      setPasswordSubmitting(false);
    }
  };

  return (
    <Container fluid className="py-3 profile-page">
      <div className="mb-3">
        <h1 className="page-title">My Profile</h1>
        <p className="page-subtitle mb-0">Your account details and security settings.</p>
      </div>
      <Row>
        <Col lg={12} xl={12}>
          <Card className="border-0 shadow-sm card-accent-top">
            <Card.Body className="profile-page-card-body">
              {/* Left Panel */}
              <div className="profile-sidebar text-center">
                <div className="profile-avatar-container">
                  <div className="profile-avatar">
                    {user?.firstName?.charAt(0)?.toUpperCase() ?? 'U'}
                    {user?.lastName?.charAt(0)?.toUpperCase() ?? ''}
                  </div>
                </div>
                <h6 className="mt-2 mb-1">{user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User'}</h6>
                <p className="text-muted small mb-2">@{user?.username || 'username'}</p>
              </div>

              {/* Main Panel */}
              <div className="profile-content">
                <div className="d-flex align-items-center mb-2">
                  <div className="icon-chip icon-chip-sm me-2"><FiUser /></div>
                  <h5 className="mb-0">Profile Details</h5>
                  <Button
                    type="button"
                    size="sm"
                    variant={isProfileEditMode ? 'secondary' : 'outline-primary'}
                    className="ms-auto"
                    onClick={() => {
                      if (isProfileEditMode) {
                        setFirstName(user?.firstName || '');
                        setLastName(user?.lastName || '');
                        setPhoneNumber(user?.phoneNumber || '');
                      }
                      setIsProfileEditMode((prev) => !prev);
                    }}
                  >
                    <FiEdit2 className="me-1" />
                    {isProfileEditMode ? 'Cancel Edit' : 'Edit'}
                  </Button>
                </div>

                {profileError && <Alert variant="danger">{profileError}</Alert>}
                {profileSuccess && <Alert variant="success">{profileSuccess}</Alert>}
                {passwordError && <Alert variant="danger">{passwordError}</Alert>}
                {passwordSuccess && <Alert variant="success">{passwordSuccess}</Alert>}

                <Form onSubmit={handleProfileSave}>
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={firstName}
                        readOnly={!isProfileEditMode}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={lastName}
                        readOnly={!isProfileEditMode}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={phoneNumber}
                        readOnly={!isProfileEditMode}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label>Email</Form.Label>
                      <Form.Control type="text" value={user?.email || 'N/A'} readOnly />
                    </Col>

                    <Col md={6}>
                      <Form.Label>Role</Form.Label>
                      <Form.Control type="text" value={roleType} readOnly />
                    </Col>

                    <Col md={6}>
                      <Form.Label>Department(s)</Form.Label>
                      <Form.Control type="text" value={departmentLabel} readOnly />
                    </Col>

                    <Col md={12}>
                      <Form.Label>Permission</Form.Label>
                      <Form.Control type="text" value={permissionsText} readOnly />
                    </Col>
                  </Row>

                  {isProfileEditMode && (
                    <div className="mt-2 d-flex gap-2">
                      <Button type="submit" disabled={profileSubmitting}>
                        {profileSubmitting ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                            Saving...
                          </>
                        ) : (
                          'Save Profile'
                        )}
                      </Button>
                    </div>
                  )}
                </Form>

                <hr className="my-3" />

                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center">
                    <div className="icon-chip icon-chip-sm icon-chip-secondary me-2"><FiLock /></div>
                    <h6 className="mb-0">Password</h6>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-primary"
                    onClick={() => setIsPasswordEditMode((prev) => !prev)}
                  >
                    <FiEdit2 className="me-1" />
                    {isPasswordEditMode ? 'Close' : 'Change Password'}
                  </Button>
                </div>

                <Form.Control type="password" value="********" readOnly />
                <small className="text-muted d-block mt-1">
                  Existing password cannot be shown for security. Use Change Password to set a new one.
                </small>

                {isPasswordEditMode && (
                  <Form onSubmit={handleChangePassword} className="mt-2">
                    <Row className="g-2">
                      <Col md={12}>
                        <Form.Label>Current Password</Form.Label>
                        <div className="d-flex">
                          <Form.Control
                            type={showCurrentPassword ? 'text' : 'password'}
                            autoComplete="off"
                            maxLength={128}
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                          />
                          <Button
                            type="button"
                            variant="outline-secondary"
                            className="ms-2"
                            onClick={() => setShowCurrentPassword((prev) => !prev)}
                          >
                            {showCurrentPassword ? <FiEyeOff /> : <FiEye />}
                          </Button>
                        </div>
                      </Col>

                      <Col md={6}>
                        <Form.Label>New Password</Form.Label>
                        <div className="d-flex">
                          <Form.Control
                            type={showNewPassword ? 'text' : 'password'}
                            autoComplete="off"
                            maxLength={128}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                          />
                          <Button
                            type="button"
                            variant="outline-secondary"
                            className="ms-2"
                            onClick={() => setShowNewPassword((prev) => !prev)}
                          >
                            {showNewPassword ? <FiEyeOff /> : <FiEye />}
                          </Button>
                        </div>
                      </Col>

                      <Col md={6}>
                        <Form.Label>Confirm Password</Form.Label>
                        <div className="d-flex">
                          <Form.Control
                            type={showConfirmPassword ? 'text' : 'password'}
                            autoComplete="off"
                            maxLength={128}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                          />
                          <Button
                            type="button"
                            variant="outline-secondary"
                            className="ms-2"
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                          >
                            {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                          </Button>
                        </div>
                      </Col>
                    </Row>

                    <Button className="mt-2" type="submit" disabled={passwordSubmitting}>
                      {passwordSubmitting ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" className="me-2" />
                          Updating...
                        </>
                      ) : (
                        'Update Password'
                      )}
                    </Button>
                  </Form>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ProfilePage;
